import type * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Receipt } from "lucide-react";
import type { ActionFieldValue } from "../ActionFieldEditor";
import {
  VendorPreviewField,
  VendorPreviewShell,
} from "./VendorPreviewShell";
import { fmtCurrencyEUR, pickField } from "./previewUtils";

/**
 * SdiInvoicePreview — SDI invoice submission preview.
 * Surfaces buyer party + invoice number + dates + amounts + XML
 * checksum so the user can verify the SDI payload BEFORE the provider signs
 * + transmits to the Agenzia delle Entrate.
 */

export interface SdiInvoicePreviewProps {
  readonly proposedFields: Readonly<Record<string, ActionFieldValue>>;
  readonly executedFields?: Readonly<Record<string, ActionFieldValue>>;
}

export const SdiInvoicePreview: React.FC<SdiInvoicePreviewProps> = ({
  proposedFields,
  executedFields,
}) => {
  const { i18n } = useLingui();
  const number = pickField(proposedFields, executedFields, "invoice_number") ||
    pickField(proposedFields, executedFields, "numero");
  const buyer = pickField(proposedFields, executedFields, "buyer_name") ||
    pickField(proposedFields, executedFields, "customer_name");
  const buyerVat = pickField(proposedFields, executedFields, "buyer_vat_id") ||
    pickField(proposedFields, executedFields, "partita_iva");
  const buyerCf = pickField(proposedFields, executedFields, "buyer_fiscal_code") ||
    pickField(proposedFields, executedFields, "codice_fiscale");
  const sdiCode = pickField(proposedFields, executedFields, "sdi_recipient_code") ||
    pickField(proposedFields, executedFields, "codice_destinatario");
  const sdiPec = pickField(proposedFields, executedFields, "sdi_pec");
  const issueDate = pickField(proposedFields, executedFields, "issue_date") ||
    pickField(proposedFields, executedFields, "data_emissione");
  const docType = pickField(proposedFields, executedFields, "document_type") ||
    pickField(proposedFields, executedFields, "tipo_documento");
  const total = executedFields?.total_amount ?? proposedFields.total_amount ??
    executedFields?.amount ?? proposedFields.amount;
  const vatAmount = executedFields?.vat_amount ?? proposedFields.vat_amount;

  return (
    <VendorPreviewShell
      icon={<Receipt className="h-4 w-4" />}
      title={
        i18n._(msg`Fattura${number ? ` n. ${number}` : ""}${docType ? ` · ${docType}` : ""}`).trim() ||
        i18n._(msg`Fattura SDI`)
      }
      caption={buyer || i18n._(msg`Sistema di Interscambio`)}
      bottomBanner={{
        label: i18n._(msg`Totale documento`),
        value: (
          <span className="text-[15px] tabular-nums">{fmtCurrencyEUR(total)}</span>
        ),
        tone: "info",
      }}
      footnote={i18n._(msg`Il sistema firmerà l'XML con il certificato Agenzia delle Entrate e lo trasmetterà allo SDI. Ricevute (NS/MC/EC) torneranno nei prossimi minuti.`)}
    >
      {/* Destinatario block — promoted: cliente + P.IVA/CF + codice SDI sit
          together so the user verifies WHO receives the e-invoice up front
          (mis-send guard, mirrors the recipient-visibility rule). */}
      <VendorPreviewField
        label={i18n._(msg`Destinatario`)}
        wide
        value={
          buyer || buyerVat || buyerCf ? (
            <div className="flex flex-col gap-0.5">
              {buyer ? (
                <span className="font-medium text-[14px] text-ink-1">{buyer}</span>
              ) : null}
              {buyerVat ? (
                <span className="font-mono text-[12px] text-ink-2">
                  {i18n._(msg`P.IVA ${buyerVat}`)}
                </span>
              ) : null}
              {buyerCf ? (
                <span className="font-mono text-[12px] text-ink-2">
                  {i18n._(msg`C.F. ${buyerCf}`)}
                </span>
              ) : null}
            </div>
          ) : null
        }
        hideEmpty
      />
      <VendorPreviewField
        label={i18n._(msg`Codice destinatario`)}
        value={sdiCode ? <span className="font-mono uppercase">{sdiCode}</span> : null}
        hideEmpty
      />
      <VendorPreviewField
        label={i18n._(msg`PEC SDI`)}
        value={sdiPec ? <span className="font-mono text-[12px]">{sdiPec}</span> : null}
        hideEmpty
      />
      <VendorPreviewField label={i18n._(msg`Data emissione`)} value={issueDate} hideEmpty />
      <VendorPreviewField
        label={i18n._(msg`IVA`)}
        value={vatAmount ? fmtCurrencyEUR(vatAmount) : null}
        hideEmpty
      />
    </VendorPreviewShell>
  );
};
SdiInvoicePreview.displayName = "SdiInvoicePreview";
